'use client';

import { useEffect, useRef, type ReactNode } from 'react';

import {
  FORM_KEY,
  openPopupTab,
  resolveFormActionFormName,
  type FormActionFormName,
  type FormKey,
} from '@publishing/modules/forms';

import { useFloatingLayer } from '@/shared/contexts/FloatingLayerContext';
import type { GentooDisplayLocation } from '@/shared/lib/gentoo';
import type { ZentoButtonMode } from '@/shared/contexts/FloatingLayerContext';

type ConsultLeadProp =
  | boolean
  | {
      /** 탭형 상담 팝업 초기 선택 상품. 미지정 시 shared-office. */
      productKey?: FormKey;
      /** 탭형이 아닌 커스텀 오픈. 지정 시 productKey 무시. */
      action?: () => void;
      /** openPopupTab formName (선택). */
      formName?: FormActionFormName;
    };

type LeadProp =
  | boolean
  | {
      /** 오피스 견적 버튼 노출 여부. 미지정 시 숨김. */
      estimate?: boolean;
      /**
       * 상담 신청 버튼 노출 및 클릭 액션.
       * - `true`: 노출. shared-office 탭형 팝업.
       * - `{ productKey }`: 해당 상품 탭 선택 상태로 탭형 팝업.
       * - `{ action }`: 커스텀 오픈 (이벤트 single, 홈 multi 등).
       * - `false` / 미지정: 숨김.
       */
      consult?: ConsultLeadProp;
      /** 전화 버튼 노출 여부. 미지정 시 숨김. */
      call?: boolean;
      /**
       * Lead 카드 전체를 대체할 커스텀 렌더러. 지정 시 기본 `<LeadButton />` 대신 사용됨.
       * 이 경우 estimate/consult/call 개별 필드는 무시됨.
       * (client component 에서만 지정 가능)
       */
      render?: () => ReactNode;
    };

/**
 * Zento 프롭 — 3-상태.
 * - `undefined`(미지정): slot 미변경 → layer 의 default(=visible) 유지
 * - `false`: 명시적 hide
 * - `true` / `{ ... }`: 노출 (render 지정 시 커스텀 컴포넌트로 교체)
 *
 * 커스텀 렌더러가 반환하는 트리거 엘리먼트에는 반드시
 * `id={GENTOO_CUSTOM_BUTTON_ID}` 를 부착해야 SDK 가 클릭을 잡는다
 * (`@/shared/lib/gentoo` 에서 import).
 *
 * `displayLocation` / `itemId` 는 Gentoo SDK boot 파라미터로 전달되어
 * 페이지 컨텍스트에 맞춘 대화를 이끌어낸다.
 */
type ZentoProp =
  | boolean
  | {
      render?: () => ReactNode;
      /** Gentoo boot displayLocation. 미지정 시 'HOME'. */
      displayLocation?: GentooDisplayLocation;
      /** displayLocation === 'PRODUCT_DETAIL' 페이지에서 상품 ID. */
      itemId?: string;
      /**
       * 트리거 버튼 UI 모드. 기본 `custom` (프로젝트 <ZentoButton />).
       * - `sdk`: Gentoo SDK 기본 플로팅 버튼 사용.
       * - `none`: 버튼 미노출 (boot 만 진행).
       */
      buttonMode?: ZentoButtonMode;
    };

type Props = {
  /** 오피스견적·상담신청·전화걸기 3-in-1 카드. 기본 invisible. */
  lead?: LeadProp;
  /**
   * 스크롤 최상단 이동 버튼 — 젠투와 동일한 3-상태. 기본 visible (설정 안 해도 노출).
   * - `undefined`(미지정): slot 미변경 → layer 의 default(=visible) 유지
   * - `false`: 명시적 hide
   * - `true`: 노출
   */
  scrollTop?: boolean;
  /** 젠투 실시간 채팅 트리거. 기본 visible (설정 안 해도 노출). */
  zento?: ZentoProp;
  /**
   * Lead 그룹(LeadButton + ScrollTop) wrapper 오버라이드.
   * 기본: `fixed right-[calc(20px+var(--scrollbar-lock-width))] top-1/2 ...`.
   */
  leadClassName?: string;
  /**
   * Zento wrapper 오버라이드.
   * 기본: `fixed right-[calc(20px+var(--scrollbar-lock-width))] bottom-56 ...`.
   */
  zentoClassName?: string;
};

/**
 * 페이지/layout 상단에 한 줄로 렌더해 플로팅 CTA 를 등록.
 * unmount 시 slot 이 리셋되어 페이지 전환에 이전 config 가 새어나가지 않음.
 * 여러 layout / page 가 중첩 등록하면 가장 안쪽(마지막 마운트) 이 이김.
 */
export function FloatingConfig({
  lead,
  scrollTop,
  zento,
  leadClassName,
  zentoClassName,
}: Props) {
  useLeadSync(lead);
  useScrollTopSync(scrollTop);
  useZentoSync(zento);
  useContainerSync(leadClassName, zentoClassName);
  return null;
}

// ────────────────────────────────────────────────────────────
// 내부 슬롯 동기화 훅
// - 슬롯 재등록은 visibility·presence 변화에만 반응 (thrash 방지)
// - 콜백/render 클로저는 useRef 로 최신값 유지
// ────────────────────────────────────────────────────────────

function useLeadSync(lead: LeadProp | undefined) {
  const { setLead } = useFloatingLayer();

  const shortcutOn = lead === true;
  const config = typeof lead === 'object' && lead !== null ? lead : undefined;

  // shortcut(`lead={true}`): 3개 모두 노출. 객체: 각 필드 opt-in (미지정 = 숨김).
  const estimate = shortcutOn || !!config?.estimate;
  const consult = shortcutOn || !!config?.consult;
  const call = shortcutOn || !!config?.call;
  const hasRender = !!config?.render;
  const consultObj =
    typeof config?.consult === 'object' ? config.consult : undefined;
  const hasCustomConsultAction = !!consultObj?.action;
  const consultProductKey = consultObj?.productKey;
  const consultFormName = consultObj?.formName;
  const anyItemVisible = estimate || consult || call;

  const actionRef = useRef<(() => void) | undefined>(undefined);
  const renderRef = useRef<(() => ReactNode) | undefined>(undefined);
  useEffect(() => {
    actionRef.current = consultObj?.action;
    renderRef.current = config?.render;
  });

  // render 지정 시엔 하위 필드 무시하고 슬롯 노출. 아니면 하나라도 켜져 있어야 노출.
  const visible = hasRender || anyItemVisible;

  useEffect(() => {
    if (!visible) {
      setLead(null);
      return;
    }

    let consultAction: (() => void) | undefined;
    if (consult) {
      if (hasCustomConsultAction) {
        consultAction = () => actionRef.current?.();
      } else {
        const key = consultProductKey ?? FORM_KEY.SHARED_OFFICE;
        consultAction = () =>
          openPopupTab(
            key,
            consultFormName
              ? { formName: resolveFormActionFormName(consultFormName) }
              : undefined,
          );
      }
    }

    setLead({
      visible: true,
      estimate,
      consult,
      call,
      consultAction,
      render: hasRender ? () => renderRef.current?.() : undefined,
    });

    return () => setLead(null);
  }, [
    visible,
    estimate,
    consult,
    call,
    hasCustomConsultAction,
    consultProductKey,
    consultFormName,
    hasRender,
    setLead,
  ]);
}

function useScrollTopSync(scrollTop: boolean | undefined) {
  const { setScrollTop } = useFloatingLayer();
  const provided = scrollTop !== undefined;
  const explicitHide = scrollTop === false;
  useEffect(() => {
    // 미지정: layer 의 default(=visible) 유지 → slot 건드리지 않음.
    if (!provided) return;
    setScrollTop({ visible: !explicitHide });
    return () => setScrollTop(null);
  }, [provided, explicitHide, setScrollTop]);
}

function useZentoSync(zento: ZentoProp | undefined) {
  const { setZento } = useFloatingLayer();
  const config =
    typeof zento === 'object' && zento !== null ? zento : undefined;

  const renderRef = useRef<(() => ReactNode) | undefined>(undefined);
  useEffect(() => {
    renderRef.current = config?.render;
  });

  const provided = zento !== undefined;
  const explicitHide = zento === false;
  const hasRender = !!config?.render;
  const displayLocation = config?.displayLocation;
  const itemId = config?.itemId;
  const buttonMode = config?.buttonMode;

  useEffect(() => {
    // 미지정: layer 의 default(=visible) 유지 → slot 건드리지 않음.
    if (!provided) return;
    if (explicitHide) {
      setZento({ visible: false });
      return () => setZento(null);
    }
    setZento({
      visible: true,
      render: hasRender ? () => renderRef.current?.() : undefined,
      displayLocation,
      itemId,
      buttonMode,
    });
    return () => setZento(null);
  }, [
    provided,
    explicitHide,
    hasRender,
    displayLocation,
    itemId,
    buttonMode,
    setZento,
  ]);
}

function useContainerSync(leadClassName?: string, zentoClassName?: string) {
  const { setContainer } = useFloatingLayer();
  useEffect(() => {
    if (!leadClassName && !zentoClassName) {
      setContainer(null);
      return;
    }
    setContainer({ leadClassName, zentoClassName });
    return () => setContainer(null);
  }, [leadClassName, zentoClassName, setContainer]);
}
